import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, DashboardModule, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import ProductSelector from '@components/ProductSelector'
import useProductSelector from '@components/ProductSelector/useProductSelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import useCreateProviderPackageItemModal from './hooks/useCreateProviderPackageItemModal'
import ProviderPackageProductListItem from './ProviderPackageProductListItem'

const MODAL_KEY = 'CreateProviderPackageItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProviderPackageItem: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateProviderPackageItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal,
    createProviderPackageItem,
  } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { providerPackageItem } = modalPayload

  const productSelectorPayload = useProductSelector()
  const { state } = productSelectorPayload

  const {
    addedProductIds,
    callbacks: {
      createProviderPackageItem: createFn,
      mapStateToEntityState,
      toggleId,
    },
    creating,
    hasAssignedProviderPackageItems,
    providerId,
    saveEnabled,
  // @ts-expect-error TS2322
  } = useCreateProviderPackageItemModal({ providerPackageItem, state })

  const providerPackageItemPayload = {
    callbacks: {
      action: createFn,
      afterAction: toggleId,
    },
    entityParams: mapStateToEntityState(),
    toastText: 'Product Added',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine="Product" />

      <SidebarModal.Content>
        <ProductSelector
          productSelectorPayload={productSelectorPayload}
          providerId={providerId}
        />

        <Button
          buttonStyle="ghostEdit"
          disabled={!saveEnabled}
          icon={<FontAwesomeIcon icon={faCheck} />}
          loading={creating}
          // @ts-expect-error TS2322
          marginTop="large"
          // @ts-expect-error TS2345
          onClick={() => createProviderPackageItem(providerPackageItemPayload)}
          size="medium"
        >
          {saveEnabled ? 'Add Product' : 'Complete Fields'}
        </Button>

        {hasAssignedProviderPackageItems && (
          <Box flexDirection="column">
            <SectionDivider boxProps={{ marginTop: 'large' }}>ADDED PRODUCTS</SectionDivider>

            <DashboardModule.ScrollableSection>
              {addedProductIds.map(id => (
                <ProviderPackageProductListItem
                  callbacks={{ toggleId }}
                  key={id}
                  providerPackageItemId={id}
                />
              ))}
            </DashboardModule.ScrollableSection>
          </Box>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondary"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateProviderPackageItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
